/**
 * Panel Route Context & Navigation Hooks
 *
 * Provides isolated route context for split view panels and unified navigation.
 * Each panel can have its own "virtual" route that differs from the browser URL.
 *
 * ## Key Concepts
 *
 * ### Panel Route Context
 * When content renders inside a split view panel, it's wrapped with
 * `PanelRouteProvider` which provides the panel's route. Hooks like
 * `usePanelSearchParams()` read from this context first, falling back
 * to the browser URL if not in a panel.
 *
 * ### Unified Reading Hooks
 * - `usePanelSearchParams()` - Get search params (panel-aware)
 * - `useResourceId()` - Get the `?id=` param (panel-aware)
 * - `useGlobalResourceId()` - Get `?id=` from browser URL only (for sidebar)
 *
 * ### Navigation
 * - `usePanelNavigation().navigateTo()` - Navigate with panel awareness
 *   - Updates panel store first (instant UI update)
 *   - Then updates browser URL
 *   - Uses navigation guard to prevent double sync
 *
 * ## Usage Examples
 *
 * ```tsx
 * // In a page component
 * function DashboardPage() {
 *   const dashboardId = useResourceId(); // Works in panel or standalone
 *   const { navigateTo } = usePanelNavigation();
 *
 *   return <button onClick={() => navigateTo('/dashboards?id=123')}>
 *     Go to dashboard
 *   </button>;
 * }
 *
 * // In sidebar (checking active state)
 * function SidebarItem({ id }) {
 *   const activeId = useGlobalResourceId(); // Uses browser URL
 *   return <div className={activeId === id ? 'active' : ''}>...</div>;
 * }
 * ```
 *
 * @see SplitViewProvider for the sync mechanism
 * @see navigation-guard.ts for preventing double sync
 */

import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { 
  useTabsStore as useSplitViewStore, 
  useFocusedPanel,
  useSplitViewActive,
  SplitPanelType,
} from "@/hooks/use-tabs-store";
import { setExpectedRoute } from "@/utils/navigation-guard";

// ============================================================================
// Types
// ============================================================================

interface PanelRouteContextValue {
  /** The route for this panel (e.g., "/dashboards?id=abc") */
  route: string;
  /** Parsed search params from the panel's route */
  searchParams: URLSearchParams;
  /** The pathname portion of the route */
  pathname: string;
  /** Whether we're inside a panel context */
  isInPanel: boolean;
}

// ============================================================================
// Context
// ============================================================================

const PanelRouteContext = createContext<PanelRouteContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface PanelRouteProviderProps {
  /** The route for this panel */
  route: string;
  /** Children to render */
  children: React.ReactNode;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Provides panel-specific route context.
 * Wrap panel content with this to give it access to its own route params.
 */
export function PanelRouteProvider({ route, children }: PanelRouteProviderProps) {
  const value = useMemo<PanelRouteContextValue>(() => {
    try {
      const url = new URL(route, window.location.origin);
      return {
        route,
        searchParams: url.searchParams,
        pathname: url.pathname,
        isInPanel: true,
      };
    } catch {
      // Fallback for invalid routes
      return {
        route,
        searchParams: new URLSearchParams(),
        pathname: route.split("?")[0] || "/",
        isInPanel: true,
      };
    }
  }, [route]);

  return (
    <PanelRouteContext.Provider value={value}>
      {children}
    </PanelRouteContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Get the panel route context (if inside a panel).
 * Returns null if not inside a PanelRouteProvider.
 */
export function usePanelRouteContext(): PanelRouteContextValue | null {
  return useContext(PanelRouteContext);
}

/**
 * Get search params - uses panel route if inside a panel, otherwise global URL.
 * This is a drop-in replacement for useSearchParams() in pages that need
 * to work both standalone and in split view panels.
 *
 * @returns URLSearchParams from the panel route or global URL
 */
export function usePanelSearchParams(): URLSearchParams {
  const panelContext = useContext(PanelRouteContext);
  const [globalParams] = useSearchParams();

  // If inside a panel, use the panel's params
  if (panelContext) {
    return panelContext.searchParams;
  }

  // Otherwise use the global URL params
  return globalParams;
}

/**
 * Get a specific search param value - works in both panel and standalone contexts.
 *
 * @param key The param key to get
 * @returns The param value or null
 */
export function usePanelSearchParam(key: string): string | null {
  const params = usePanelSearchParams();
  return params.get(key);
}

/**
 * Get the current resource ID from the URL or panel route.
 * This is the unified hook for getting the `id` query parameter.
 * 
 * Usage:
 * - In split view panels: reads from panel's route
 * - Standalone pages: reads from browser URL
 * - Sidebar components (checking active state): use `useGlobalResourceId` instead
 * 
 * @returns The resource ID or null if not present
 */
export function useResourceId(): string | null {
  return usePanelSearchParam("id");
}

/**
 * Get the current resource ID from the global browser URL only.
 * Use this for components that need to check against the browser URL,
 * like sidebar items checking if they're "active".
 * 
 * For most page content, use `useResourceId()` instead which is panel-aware.
 * 
 * @returns The resource ID from browser URL or null
 */
export function useGlobalResourceId(): string | null {
  const [globalParams] = useSearchParams();
  return globalParams.get("id");
}

/**
 * Check if we're currently inside a split view panel.
 */
export function useIsInPanel(): boolean {
  const context = useContext(PanelRouteContext);
  return context?.isInPanel ?? false;
}

/**
 * Get the pathname - from panel route if in panel, otherwise from window.
 */
export function usePanelPathname(): string {
  const panelContext = useContext(PanelRouteContext);

  if (panelContext) {
    return panelContext.pathname;
  }

  return window.location.pathname;
}

// ============================================================================
// Panel Navigation Hook
// ============================================================================

interface PanelNavigationOptions {
  /** Replace current history entry instead of pushing */
  replace?: boolean;
  /** Optional title update for the panel */
  title?: string;
}

interface PanelNavigationResult {
  /** Navigate to a route - updates panel route if in split view, otherwise global URL */
  navigateTo: (route: string, options?: PanelNavigationOptions) => void;
  /** Whether we're inside a split view panel */
  isInPanel: boolean;
  /** Whether split view is currently active */
  isSplitViewActive: boolean;
  /** The index of the current panel (if in panel) */
  panelIndex: number | null;
  /** Whether a navigation transition is pending (use for showing loading states) */
  isPending: boolean;
}

/**
 * Hook for panel-aware navigation.
 * 
 * Works in two modes:
 * 1. Inside a panel (via PanelRouteContext): updates the current panel's route
 * 2. Outside panels (e.g., sidebar): updates the focused panel's route when split view is active
 * 
 * When split view is inactive, always uses global URL navigation.
 * 
 * This ensures that navigating within a panel doesn't affect other panels,
 * and sidebar navigation affects only the focused panel in split view.
 */
export function usePanelNavigation(): PanelNavigationResult {
  const panelContext = useContext(PanelRouteContext);
  const navigate = useNavigate();
  
  
  // Get split view state - use optimized hooks
  const focusedPanel = useFocusedPanel();
  const isSplitViewActive = useSplitViewActive();
  const updatePanel = useSplitViewStore((s) => s.updatePanel);
  
  const isInPanel = panelContext?.isInPanel ?? false;
  const focusedPanelId = focusedPanel?.id ?? null;
  
  // Find the panel ID:
  // - If inside a panel and route matches focused panel, use focused panel
  // - If outside (sidebar), use focusedPanelId
  const targetPanelId = useMemo(() => {
    if (isInPanel && panelContext?.route && focusedPanel) {
      // If we're in a panel and the route matches the focused panel, use it
      if (focusedPanel.route === panelContext.route) {
        return focusedPanel.id;
      }
      // Otherwise use the focused panel anyway (simplified logic)
      return focusedPanelId;
    }
    // Outside panels - use focused panel if split view is active
    return isSplitViewActive ? focusedPanelId : null;
  }, [isInPanel, panelContext?.route, focusedPanel, focusedPanelId, isSplitViewActive]);
  
  // Panel index is always 0 or null since we only track the focused panel
  const panelIndex = targetPanelId && focusedPanel ? 0 : null;
  
  const navigateTo = useCallback(
    (route: string, options?: PanelNavigationOptions) => {
      const { replace = false, title } = options || {};
      
      // Mark this route as expected to prevent syncWithRoute from re-syncing
      // This is critical for preventing double sync loops
      // Must be set BEFORE navigate() to ensure the guard is in place
      setExpectedRoute(route);
      
      // Update the browser URL immediately for instant feedback
      navigate(route, { replace });
      
      // If there's a focused panel, update it
      if (focusedPanelId) {
        // Derive the new panel type from the route
        const newType = getPanelTypeFromRoute(route.split("?")[0]);
        
        // Build the update object
        const panelUpdate: { route: string; type: SplitPanelType; title?: string } = {
          route,
          type: newType,
        };
        if (title) {
          panelUpdate.title = title;
        }
        
        // Update the panel's route and type in the store
        updatePanel(focusedPanelId, panelUpdate);
      }
    },
    [focusedPanelId, updatePanel, navigate]
  );
  
  return {
    navigateTo,
    isInPanel,
    isSplitViewActive,
    panelIndex,
    isPending: false, // TODO: Implement actual pending state tracking
  };
}

/**
 * Derive a panel type from a route pathname.
 */
export function getPanelTypeFromRoute(pathname: string): SplitPanelType {
  if (pathname.startsWith("/dashboards")) return "dashboard";
  if (pathname.startsWith("/trackers")) return "trackers";
  if (pathname.startsWith("/alerts")) return "alerts";
  if (pathname.startsWith("/enrichments")) return "enrichments";
  if (pathname.startsWith("/chat")) return "chat";
  if (pathname.startsWith("/search")) return "search";
  if (pathname.startsWith("/account")) return "account";
  if (pathname.startsWith("/widgets")) return "widgets";
  if (pathname.startsWith("/branches")) return "branches";
  if (pathname.startsWith("/investigations")) return "investigations";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/workspaces")) return "workspaces";
  if (pathname.startsWith("/tokens")) return "tokens";
  if (pathname.startsWith("/members")) return "members";
  if (pathname.startsWith("/channels")) return "channels";
  if (pathname.startsWith("/organization")) return "organization";
  if (pathname.startsWith("/settings") || pathname.startsWith("/plan")) return "settings";
  return "home";
}

// ============================================================================
// Exports
// ============================================================================

export { PanelRouteContext };
export type { PanelRouteContextValue, PanelRouteProviderProps, PanelNavigationOptions, PanelNavigationResult };
